import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import {
  WalletInterface,
  WalletTokenInterface,
  WalletUnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import ApiService, { handleUnauthorized } from '@/services/ApiService';
import { logError, logInfo } from '@/utils/logger';
import { normalizeWalletsResponse } from './normalizeWalletsResponse';
import { loadWalletsStub } from '@/utils/stubLoader';
const WALLET_REQUEST_CACHE = new Map<
  string,
  {
    timestamp: number;
    promise: Promise<WalletInterface[]>;
    inProgress: boolean;
  }
>();
const WALLET_THROTTLE_TIME_MS =
  APP_CONFIG.THROTTLE.WALLETS_THROTTLE_DURATION_IN_MS;
export const fetchWallets = async (): Promise<WalletInterface[]> => {
  const cacheKey = ENDPOINTS.USER_WALLETS;
  const now = Date.now();
  const cached = WALLET_REQUEST_CACHE.get(cacheKey);
  if (
    cached &&
    (cached.inProgress || now - cached.timestamp <= WALLET_THROTTLE_TIME_MS)
  ) {
    logInfo('Using cached wallet request', 'WalletsService.fetchWallets');
    return cached.promise;
  }
  const promise = (async () => {
    try {
      let response: any = null;
      const shouldUseStub =
        process.env.NODE_ENV === 'development' &&
        APP_CONFIG.STUBS?.USE_WALLETS_STUB;
      if (shouldUseStub) {
        response = await loadWalletsStub();
        if (response) {
          logInfo('Using stub data for wallets', 'WalletsService.fetchWallets');
        }
      }
      if (!response) {
        logInfo('Fetching wallets from API', 'WalletsService.fetchWallets');
        response = await ApiService.get(cacheKey);
      }
      if (response.isBetaRestricted) {
        logInfo(
          'Beta restriction encountered for wallets',
          'WalletsService.fetchWallets',
          response,
        );
        handleUnauthorized();
        return [];
      }
      logInfo(
        'Fetched wallets successfully',
        'WalletsService.fetchWallets',
        response,
      );
      if (!Array.isArray(response)) {
        logError(
          'Response is not an array',
          'WalletsService.fetchWallets',
          response,
        );
        return [];
      }
      return normalizeWalletsResponse(response);
    } catch (error: any) {
      if (error.isBetaRestricted) {
        logInfo(
          'Beta restriction encountered for wallets',
          'WalletsService.fetchWallets',
          error,
        );
        handleUnauthorized();
        return [];
      }
      if (error.response) {
        const { status, data } = error.response;
        logError(
          `API error encountered - HTTP ${status}`,
          'WalletsService.fetchWallets',
          error,
        );
        if (
          status === 401 &&
          data?.errorCode === 'UNAUTHORIZED' &&
          data?.errorMessage === 'Not part of beta program'
        ) {
          logInfo(
            'Beta restriction encountered for wallets',
            'WalletsService.fetchWallets',
            error,
          );
          handleUnauthorized();
          return [];
        }
        throw error;
      }
      logError(error, 'WalletsService.fetchWallets');
      throw error;
    }
  })();
  WALLET_REQUEST_CACHE.set(cacheKey, {
    timestamp: now,
    promise,
    inProgress: true,
  });
  promise
    .then((result) => {
      const entry = WALLET_REQUEST_CACHE.get(cacheKey);
      if (entry && entry.timestamp === now) {
        WALLET_REQUEST_CACHE.set(cacheKey, {
          ...entry,
          inProgress: false,
        });
      }
      return result;
    })
    .catch((error) => {
      logError('Error in promise chain', 'WalletsService.fetchWallets', error);
      return [];
    });
  setTimeout(() => {
    const entry = WALLET_REQUEST_CACHE.get(cacheKey);
    if (entry && entry.timestamp === now && !entry.inProgress) {
      WALLET_REQUEST_CACHE.delete(cacheKey);
    }
  }, WALLET_THROTTLE_TIME_MS);
  return promise;
};
export const withdrawFromWallet = async (
  sourceWallet: WalletInterface,
  token: WalletTokenInterface,
  amountInTokenCurrency: number,
  destinationWalletAddress: string,
): Promise<any> => {
  logInfo('Initiating withdrawal', 'WalletsService.withdrawFromWallet', {
    walletAddress: sourceWallet.address,
    blockchain: sourceWallet.blockchain,
    amountInTokenCurrency,
    destinationWalletAddress,
  });
  const requestBody = {
    destinationAddress: destinationWalletAddress,
    token: {
      blockchain: token.blockchain,
      address: token.address,
    },
    amount: amountInTokenCurrency.toString(),
  };
  try {
    const response = await ApiService.post(
      ENDPOINTS.WITHDRAW_FROM_WALLET,
      requestBody,
    );
    logInfo(
      'Withdrawal successful',
      'WalletsService.withdrawFromWallet',
      response,
    );
    return response;
  } catch (error) {
    logError(error, 'WalletsService.withdrawFromWallet');
    throw new Error('Withdrawal failed');
  }
};
export const fetchWithdrawalableTokens = async (): Promise<
  (WalletTokenInterface | WalletUnderlyingYieldBearingAssetInterface)[]
> => {
  const wallets = await fetchWallets();
  const allTokens = wallets.flatMap((wallet) => [
    ...wallet.balances,
    ...wallet.underlyingYieldBearingAssetBalances,
  ]);
  return allTokens;
};
